import { createContext, useContext, useId, useState, type ReactNode } from 'react'

import { t } from '@ctcells/i18n'

import { Button } from '../../atoms/Button.tsx'
import { Combobox } from '../../atoms/Combobox.tsx'
import { Disclosure as DisclosureRow } from '../../atoms/Disclosure.tsx'
import { Field } from '../../atoms/Field.tsx'
import { Icon } from '../../atoms/Icon.tsx'
import { Segmented } from '../../atoms/Segmented.tsx'
import { TextInput } from '../../atoms/TextInput.tsx'
import { Tabs } from '../../layout/Tabs.tsx'
import type { ActionGate } from '../access.ts'
import {
  ASSEMBLER_OPTIONAL,
  ASSEMBLER_REQUIRED,
  CENTRIFUGE_OPTIONAL,
  CENTRIFUGE_REQUIRED,
  CONSTRAINT_FIELDS,
  emptyStep,
  JSON_SECTIONS,
  MODULE_STEP_OPTIONAL,
  moduleStepRequired,
  moveStep,
  STAINING_OPTIONAL,
  STAINING_STEP_OPTIONAL,
  STAINING_STEP_REQUIRED,
  stainingConditionalRequired,
  stainingForbidden,
  recipeIssueKey,
  type DraftFields,
  type DraftIssue,
  type DraftStep,
  type EditorGroup,
  type FieldSpec,
  type RecipeDraft,
  type StepSection,
} from './recipeDraft.ts'

import './recipeEditor.css'

/**
 * 「새 레시피 › 값 직접 설정」 편집기 — **서버가 받는 본문을 칸으로 적는다**(2026-09-29 되살림).
 *
 * 🔴 칸은 `recipe.schema.json` 이 정한다(`recipeDraft.ts`). 필수 칸이 먼저 서고 선택 칸은 접혀 있다.
 * 🔴 드롭다운·토글·입력은 앱 원자(`Combobox`·`Segmented`·`TextInput`)다 — 브라우저 기본 모양을 내놓지 않는다.
 * 🔴 권한이 없으면 칸을 숨기지 않는다 — 전부 비활성 + 사유다(`edit` 게이트).
 * ⚠ 이 파일은 상태를 쥐지 않는다. 초안은 화면(`RecipeNewScreen`)이 쥐고, 짓기·판정은 `recipeDraft.ts` 가 한다.
 *
 * 🔴 **한 번에 한 묶음만 보인다**(eric 2026-09-30 「내용이 너무 많은데.. 불러오기 하면 너무 지저분해지는데..」).
 *    종전에는 절 넷이 세로로 쌓여 표준 레시피(스텝 229개)를 불러오면 본문이 1만 4천 px 였다. 지금은
 *    ⓐ 절 넷이 탭이고 ⓑ 스텝 · 선택 칸 · JSON 절은 **기본 접힘**이다. 접는 것은 «보이기»뿐이다 —
 *    접힌 칸의 값도 초안에 그대로 있고 그대로 나간다(본문은 `buildRecipePayload` 가 초안에서 짓는다).
 * 🔴 **안 보이는 곳의 문제가 숨지 않는다.** 고칠 것이 든 탭 · 접힌 스텝 · 접힌 선택 칸에 글리프 + 건수를 단다
 *    (`DraftIssue.at`). 색만으로 말하지 않는다 — 삼각 글리프 · 숫자 · 보조기술용 「고칠 것 N곳」이 같이 간다.
 */

type ValidationField = (key: string) => {
  inputProps: { id: string; invalid: boolean; describedBy: string | undefined }
  fieldProps: { htmlFor: string; error: string | undefined; errorId: string }
}
const EditorValidation = createContext<{
  field: ValidationField | undefined
  revealErrors: boolean
  locate: (fields: DraftFields, path: string) => string
} | null>(null)

type Gate = ActionGate
type Issues = readonly DraftIssue[]

// ── 표지 · 접이 ────────────────────────────────────────────────────────────

/** 고칠 것 N곳 — 0 이면 그리지 않는다 */
function IssueMark({ count }: { count: number }) {
  if (count === 0) return null
  const text = t('recipeEdit.issueMark', { count })
  return (
    <span className="redit-mark" title={text}>
      <Icon name="alert" size="sm" />
      <span aria-hidden="true">{String(count)}</span>
      <span className="a-sr">{text}</span>
    </span>
  )
}

/**
 * 접힌 줄 하나(`atoms/Disclosure`) — 🔴 접혀 있어도 **무엇이 들었는지**(`meta`)와 **고칠 것**이 줄에 보인다.
 * 펼치기는 권한과 무관하다(보는 일이다) — 안의 칸이 게이트를 따른다.
 */
function Disclosure({
  label,
  meta,
  issues,
  children,
}: {
  label: string
  meta: string
  issues: number
  children: ReactNode
}) {
  const validation = useContext(EditorValidation)
  return (
    <DisclosureRow
      defaultOpen={validation?.revealErrors === true && issues > 0}
      extra={<IssueMark count={issues} />}
      label={label}
      meta={meta}
    >
      {children}
    </DisclosureRow>
  )
}

// ── 칸 하나 ────────────────────────────────────────────────────────────────

interface FieldInputProps {
  spec: FieldSpec
  value: string
  onChange: (value: string) => void
  gate: Gate
  id: string
  invalid?: boolean | undefined
  describedBy?: string | undefined
}

function FieldInput({ spec, value, onChange, gate, id, invalid, describedBy }: FieldInputProps) {
  const label = t(spec.label)
  /*
   * ⚠ `Combobox` · `Segmented` 에는 비활성 사유 자리가 없다 — 권한이 없으면 같은 값을 **비활성 입력**으로
   *   보인다. 칸이 사라지지 않고 사유가 읽힌다.
   */
  if (gate.disabled && (spec.kind === 'enum' || spec.kind === 'bool')) {
    const shown =
      spec.kind === 'bool'
        ? value === 'true'
          ? t('value.yes')
          : value === 'false'
            ? t('value.no')
            : ''
        : value
    return <TextInput id={id} onChange={onChange} value={shown} wide {...gate} />
  }

  switch (spec.kind) {
    case 'enum':
      return (
        <Combobox
          id={id}
          invalid={invalid}
          describedBy={describedBy}
          label={label}
          onChange={onChange}
          options={spec.options ?? []}
          toggleLabel={t('recipeEdit.options.toggle', { field: label })}
          value={value}
        />
      )
    case 'bool':
      return (
        <Segmented
          id={id}
          invalid={invalid}
          describedBy={describedBy}
          label={t('recipeEdit.bool.label', { field: label })}
          onChange={onChange}
          options={[
            // 🔴 「비움」이 첫 자리다 — 기본값을 지어내지 않는다(적지 않으면 본문에서 빠진다)
            { value: '', label: t('mask.none') },
            { value: 'true', label: t('value.yes') },
            { value: 'false', label: t('value.no') },
          ]}
          value={value}
        />
      )
    case 'json':
      return (
        <JsonArea
          invalid={invalid}
          describedBy={describedBy}
          gate={gate}
          id={id}
          onChange={onChange}
          value={value}
        />
      )
    default:
      return (
        <TextInput
          id={id}
          invalid={invalid}
          describedBy={describedBy}
          inputMode={spec.kind === 'int' || spec.kind === 'number' ? 'decimal' : undefined}
          onChange={onChange}
          value={value}
          wide
          {...gate}
        />
      )
  }
}

function JsonArea({
  id,
  value,
  onChange,
  gate,
  label,
  invalid,
  describedBy,
}: {
  invalid?: boolean | undefined
  describedBy?: string | undefined
  id?: string
  value: string
  onChange: (value: string) => void
  gate: Gate
  label?: string
}) {
  return (
    <textarea
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      aria-disabled={gate.disabled ? true : undefined}
      aria-label={label}
      className="jsonin redit__json"
      id={id}
      onChange={gate.disabled ? undefined : (e) => onChange(e.target.value)}
      readOnly={gate.disabled}
      spellCheck={false}
      title={gate.disabled ? gate.disabledReason : undefined}
      value={value}
    />
  )
}

function FieldCell({
  spec,
  fields,
  onText,
  gate,
}: {
  spec: FieldSpec
  fields: DraftFields
  onText: (path: string, value: string) => void
  gate: Gate
}) {
  const localId = useId()
  const context = useContext(EditorValidation)
  const field = context?.field?.(context.locate(fields, spec.path))
  const id = field?.inputProps.id ?? localId
  return (
    <div className={spec.kind === 'json' ? 'redit__cell redit__cell--full' : 'redit__cell'}>
      <Field
        htmlFor={id}
        label={t(spec.label)}
        error={field?.fieldProps.error}
        errorId={field?.fieldProps.errorId}
      >
        <FieldInput
          gate={gate}
          id={id}
          invalid={field?.inputProps.invalid}
          describedBy={field?.inputProps.describedBy}
          onChange={(value) => onText(spec.path, value)}
          spec={spec}
          value={fields.text[spec.path] ?? ''}
        />
      </Field>
    </div>
  )
}

function FieldGrid(props: {
  specs: readonly FieldSpec[]
  fields: DraftFields
  onText: (path: string, value: string) => void
  gate: Gate
}) {
  if (props.specs.length === 0) return null
  return (
    <div className="redit__grid">
      {props.specs.map((spec) => (
        <FieldCell
          fields={props.fields}
          gate={props.gate}
          key={spec.path}
          onText={props.onText}
          spec={spec}
        />
      ))}
    </div>
  )
}

/**
 * 선택 칸 묶음 — 🔴 **늘 접혀서 시작한다**(2026-09-30 · 종전에는 채운 칸이 있으면 펼친 채 시작해 불러오기가
 * 본문을 늘렸다). 접힌 줄이 「N곳 적힘」과 고칠 것을 말한다 — 값이 숨지 않는다.
 */
function OptionalBlock(props: {
  specs: readonly FieldSpec[]
  fields: DraftFields
  onText: (path: string, value: string) => void
  gate: Gate
  /** 이 묶음(같은 스텝 · 같은 절)의 고칠 것 — 여기서 선택 칸 경로로 거른다 */
  issues: Issues
}) {
  if (props.specs.length === 0) return null
  const paths = new Set(props.specs.map((s) => s.path))
  const filled = props.specs.filter((s) => (props.fields.text[s.path] ?? '') !== '').length
  const issues = props.issues.filter((i) => i.at.path !== undefined && paths.has(i.at.path)).length
  return (
    <Disclosure
      issues={issues}
      label={t('recipeEdit.optional')}
      meta={filled > 0 ? t('recipeEdit.filled', { count: filled }) : t('recipeEdit.empty')}
    >
      <FieldGrid {...props} />
    </Disclosure>
  )
}

const patchText = (fields: DraftFields, path: string, value: string): DraftFields => ({
  ...fields,
  text: { ...fields.text, [path]: value },
})

// ── 스텝 목록 ──────────────────────────────────────────────────────────────

interface StepListProps {
  title: string
  steps: readonly DraftStep[]
  onChange: (next: DraftStep[]) => void
  gate: Gate
  /** 이 절의 고칠 것 — 스텝 key 로 줄마다 가른다 */
  issues: Issues
  /** 목록 자체의 문제(「스텝이 하나 이상 있어야 합니다」) 건수 */
  listIssues?: number
  /** 줄 이름(보조기술 · 버튼 이름). 염색 탭은 목록이 둘이라 「스텝 1」이 두 번 나오지 않게 가른다 */
  itemName?: (n: number) => string
  /** 접힌 줄에 보일 요약 — 스텝 ID · 시퀀스(동작) */
  summaryOf: (step: DraftStep) => string
  renderBody: (step: DraftStep, onStep: (next: DraftStep) => void) => ReactNode
}

/**
 * 스텝 목록. 🔴 **모든 줄이 접혀서 시작하고 한 번에 하나만 펼친다** — 불러온 레시피는 스텝이 100개를 넘는다.
 * 접힌 줄은 요약 한 줄 + 고칠 것 표지뿐이다. 순서 바꾸기 · 삭제는 **펼친 줄에만** 선다(줄마다 버튼 넷이
 * 100번 반복되던 것이 화면을 덮었다 — before 캡처).
 */
function StepList({
  title,
  steps,
  onChange,
  gate,
  issues,
  listIssues = 0,
  itemName = (n) => t('recipeEdit.stepN', { n }),
  summaryOf,
  renderBody,
}: StepListProps) {
  const validation = useContext(EditorValidation)
  const [openKey, setOpenKey] = useState<string | null>(
    validation?.revealErrors
      ? (issues.find((i) => i.at.stepKey !== undefined)?.at.stepKey ?? null)
      : null,
  )
  const baseId = useId()

  const replace = (index: number, next: DraftStep) =>
    onChange(steps.map((s, i) => (i === index ? next : s)))
  const issuesOf = (key: string) => issues.filter((i) => i.at.stepKey === key)

  return (
    <div className="redit__block">
      <div className="redit__listhead">
        <span className="redit__listtitle">
          {title}
          <span className="redit__listcount">{String(steps.length)}</span>
          <IssueMark count={listIssues} />
        </span>
        <Button
          onClick={() => {
            const step = emptyStep()
            onChange([...steps, step])
            // 새로 더한 스텝은 펼쳐서 — 적을 칸이 바로 보인다
            setOpenKey(step.key)
          }}
          size="pill"
          {...gate}
        >
          {t('recipeEdit.action.addStep')}
        </Button>
      </div>
      {steps.length === 0 ? (
        <p className="redit__none">{t('recipeEdit.noStepsYet')}</p>
      ) : (
        <ol className="redit__steplist">
          {steps.map((step, index) => {
            const open = openKey === step.key
            const name = itemName(index + 1)
            const summary = summaryOf(step) || t('mask.none')
            const bodyId = `${baseId}-${step.key}`
            const mine = issuesOf(step.key)
            return (
              <li className={open ? 'redit__step redit__step--open' : 'redit__step'} key={step.key}>
                <div className="redit__stepline">
                  <button
                    aria-controls={open ? bodyId : undefined}
                    aria-expanded={open}
                    aria-label={`${name} · ${summary}${mine.length > 0 ? ` · ${t('recipeEdit.issueMark', { count: mine.length })}` : ''}`}
                    className="redit__steptoggle"
                    onClick={() => setOpenKey(open ? null : step.key)}
                    title={summary}
                    type="button"
                  >
                    <span aria-hidden="true" className="redit__chev" />
                    <span className="redit__stepno mono">{String(index + 1)}</span>
                    <span className="redit__stepsum mono">{summary}</span>
                    <IssueMark count={mine.length} />
                  </button>
                  {open ? (
                    <span className="redit__stepbtns">
                      <Button
                        aria-label={`${name} ${t('recipeEdit.action.moveUp')}`}
                        onClick={() => onChange(moveStep(steps, index, -1))}
                        size="pill"
                        {...(gate.disabled
                          ? gate
                          : index === 0
                            ? { disabled: true, disabledReason: t('recipeEdit.reason.firstStep') }
                            : {})}
                      >
                        {t('recipeEdit.action.moveUp')}
                      </Button>
                      <Button
                        aria-label={`${name} ${t('recipeEdit.action.moveDown')}`}
                        onClick={() => onChange(moveStep(steps, index, 1))}
                        size="pill"
                        {...(gate.disabled
                          ? gate
                          : index === steps.length - 1
                            ? { disabled: true, disabledReason: t('recipeEdit.reason.lastStep') }
                            : {})}
                      >
                        {t('recipeEdit.action.moveDown')}
                      </Button>
                      <Button
                        aria-label={`${name} ${t('recipeEdit.action.removeStep')}`}
                        onClick={() => onChange(steps.filter((_, i) => i !== index))}
                        size="pill"
                        {...gate}
                      >
                        {t('recipeEdit.action.removeStep')}
                      </Button>
                    </span>
                  ) : null}
                </div>
                {open ? (
                  <div className="redit__stepbody" id={bodyId}>
                    {renderBody(step, (next) => replace(index, next))}
                  </div>
                ) : null}
              </li>
            )
          })}
        </ol>
      )}
    </div>
  )
}

const summaryText = (step: DraftStep, keys: readonly string[]) =>
  keys
    .map((k) => (step.text[k] ?? '').trim())
    .filter((v) => v !== '')
    .join(' · ')

function ModuleStepBody({
  section,
  step,
  onStep,
  gate,
  issues,
}: {
  section: StepSection
  step: DraftStep
  onStep: (next: DraftStep) => void
  gate: Gate
  issues: Issues
}) {
  const onText = (path: string, value: string) =>
    onStep({ ...step, ...patchText(step, path, value) })
  return (
    <>
      <FieldGrid fields={step} gate={gate} onText={onText} specs={moduleStepRequired(section)} />
      <OptionalBlock
        fields={step}
        gate={gate}
        issues={issues.filter((i) => i.at.stepKey === step.key)}
        onText={onText}
        specs={MODULE_STEP_OPTIONAL}
      />
    </>
  )
}

/**
 * 염색 스텝 — **동작이 칸을 정한다.** 동작이 요구하는 칸은 필수 줄로 올라오고(스키마 `allOf … then required`),
 * 금지하는 칸은 사라진다(`then: not required`). 동작을 아직 안 골랐으면 전부 선택 칸이다.
 */
function StainingStepBody({
  step,
  onStep,
  gate,
  issues,
}: {
  step: DraftStep
  onStep: (next: DraftStep) => void
  gate: Gate
  issues: Issues
}) {
  const onText = (path: string, value: string) =>
    onStep({ ...step, ...patchText(step, path, value) })
  const operation = (step.text['operation'] ?? '').trim()
  const conditional = new Set(stainingConditionalRequired(operation, step.text['to_waste'] ?? ''))
  const forbidden = new Set(stainingForbidden(operation))
  const promoted = STAINING_STEP_OPTIONAL.filter((s) => conditional.has(s.path))
  const rest = STAINING_STEP_OPTIONAL.filter(
    (s) => !conditional.has(s.path) && !forbidden.has(s.path),
  )
  return (
    <>
      <FieldGrid
        fields={step}
        gate={gate}
        onText={onText}
        specs={[...STAINING_STEP_REQUIRED, ...promoted]}
      />
      <OptionalBlock
        fields={step}
        gate={gate}
        issues={issues.filter((i) => i.at.stepKey === step.key)}
        onText={onText}
        specs={rest}
      />
    </>
  )
}

// ── 편집기 ─────────────────────────────────────────────────────────────────

export interface RecipeEditorProps {
  draft: RecipeDraft
  onChange: (next: RecipeDraft) => void
  /** 편집 게이트 — `control` 권한. 비활성이면 칸마다 사유가 붙는다 */
  gate: Gate
  /**
   * 초안의 고칠 것(`buildRecipePayload(draft).issues`) — 안 연 탭 · 접힌 줄에 표지를 단다.
   * 권한이 없으면 부르는 쪽이 비워 넘긴다(편다고 고칠 수 없다 — 「만들기」의 사유가 먼저 말한다).
   */
  issues?: Issues
  validationField?: ValidationField
  /** 제출 검증이 실패했을 때만 오류가 든 탭과 접힌 줄을 펼친다 */
  revealErrors?: boolean
}

const GROUP_LABEL = {
  centrifuge: 'section.centrifuge',
  assembler: 'section.assembler',
  staining: 'section.staining',
  other: 'recipeEdit.card.otherSections',
} as const

const GROUPS: readonly EditorGroup[] = ['centrifuge', 'assembler', 'staining', 'other']

/** JSON 절의 줄 수 — 접힌 줄에 「적힘 · N줄」로 보인다 */
const lineCount = (text: string) => text.split('\n').length

export function RecipeEditor({
  draft,
  onChange,
  gate,
  issues = [],
  validationField,
  revealErrors = false,
}: RecipeEditorProps) {
  const [group, setGroup] = useState<EditorGroup>(
    revealErrors ? (issues[0]?.at.group ?? 'centrifuge') : 'centrifuge',
  )
  const cent = draft.centrifuge
  const asm = draft.assembler
  const stain = draft.staining
  const of = (g: EditorGroup) => issues.filter((i) => i.at.group === g)
  /** 절 칸(스텝 밖)의 고칠 것 */
  const sectionIssues = (g: EditorGroup) => of(g).filter((i) => i.at.stepKey === undefined)
  const stainModuleKeys = new Set(stain.moduleSteps.map((s) => s.key))

  const panel = (() => {
    switch (group) {
      case 'centrifuge':
        return (
          <>
            <div className="redit__block">
              <FieldGrid
                fields={cent}
                gate={gate}
                onText={(p, v) =>
                  onChange({ ...draft, centrifuge: { ...cent, ...patchText(cent, p, v) } })
                }
                specs={CENTRIFUGE_REQUIRED}
              />
              <OptionalBlock
                fields={cent}
                gate={gate}
                issues={sectionIssues('centrifuge')}
                onText={(p, v) =>
                  onChange({ ...draft, centrifuge: { ...cent, ...patchText(cent, p, v) } })
                }
                specs={CENTRIFUGE_OPTIONAL}
              />
            </div>
            <StepList
              gate={gate}
              issues={of('centrifuge')}
              onChange={(steps) => onChange({ ...draft, centrifuge: { ...cent, steps } })}
              renderBody={(step, onStep) => (
                <ModuleStepBody
                  gate={gate}
                  issues={of('centrifuge')}
                  onStep={onStep}
                  section="centrifuge"
                  step={step}
                />
              )}
              steps={cent.steps}
              summaryOf={(s) => summaryText(s, ['step_id', 'sequence', 'round'])}
              title={t('recipeEdit.steps')}
            />
          </>
        )
      case 'assembler':
        return (
          <>
            <div className="redit__block">
              <FieldGrid
                fields={asm}
                gate={gate}
                onText={(p, v) =>
                  onChange({ ...draft, assembler: { ...asm, ...patchText(asm, p, v) } })
                }
                specs={ASSEMBLER_REQUIRED}
              />
              <OptionalBlock
                fields={asm}
                gate={gate}
                issues={sectionIssues('assembler')}
                onText={(p, v) =>
                  onChange({ ...draft, assembler: { ...asm, ...patchText(asm, p, v) } })
                }
                specs={ASSEMBLER_OPTIONAL}
              />
            </div>
            <StepList
              gate={gate}
              issues={of('assembler')}
              onChange={(steps) => onChange({ ...draft, assembler: { ...asm, steps } })}
              renderBody={(step, onStep) => (
                <ModuleStepBody
                  gate={gate}
                  issues={of('assembler')}
                  onStep={onStep}
                  section="assembler"
                  step={step}
                />
              )}
              steps={asm.steps}
              summaryOf={(s) => summaryText(s, ['step_id', 'sequence'])}
              title={t('recipeEdit.steps')}
            />
          </>
        )
      case 'staining': {
        // 염색은 목록이 둘이다 — 스텝(`steps`)과 모듈 스텝(`module_steps`). 고칠 것을 key 로 가른다
        const stainIssues = of('staining')
        return (
          <>
            {/* 염색 절에는 필수 칸이 없다 — 선택 칸 묶음 하나가 절 칸 전부다 */}
            <div className="redit__block">
              <OptionalBlock
                fields={stain}
                gate={gate}
                issues={sectionIssues('staining')}
                onText={(p, v) =>
                  onChange({ ...draft, staining: { ...stain, ...patchText(stain, p, v) } })
                }
                specs={STAINING_OPTIONAL}
              />
            </div>
            <StepList
              gate={gate}
              issues={stainIssues.filter(
                (i) => i.at.stepKey !== undefined && !stainModuleKeys.has(i.at.stepKey),
              )}
              listIssues={
                stainIssues.filter((i) => i.at.stepKey === undefined && i.at.path === 'steps')
                  .length
              }
              onChange={(steps) => onChange({ ...draft, staining: { ...stain, steps } })}
              renderBody={(step, onStep) => (
                <StainingStepBody gate={gate} issues={stainIssues} onStep={onStep} step={step} />
              )}
              steps={stain.steps}
              summaryOf={(s) => summaryText(s, ['step_id', 'operation', 'target_wells'])}
              title={t('recipeEdit.steps')}
            />
            <StepList
              gate={gate}
              issues={stainIssues.filter(
                (i) => i.at.stepKey !== undefined && stainModuleKeys.has(i.at.stepKey),
              )}
              onChange={(moduleSteps) =>
                onChange({ ...draft, staining: { ...stain, moduleSteps } })
              }
              renderBody={(step, onStep) => (
                <ModuleStepBody
                  gate={gate}
                  issues={stainIssues}
                  onStep={onStep}
                  section="staining"
                  step={step}
                />
              )}
              itemName={(n) => `${t('recipeEdit.moduleSteps')} ${String(n)}`}
              steps={stain.moduleSteps}
              summaryOf={(s) => summaryText(s, ['step_id', 'sequence'])}
              title={t('recipeEdit.moduleSteps')}
            />
          </>
        )
      }
      case 'other': {
        const otherIssues = of('other')
        return (
          <>
            <div className="redit__block">
              <FieldGrid
                fields={draft.constraints}
                gate={gate}
                onText={(p, v) =>
                  onChange({ ...draft, constraints: patchText(draft.constraints, p, v) })
                }
                specs={CONSTRAINT_FIELDS}
              />
            </div>
            {/*
              🔴 **이 절들은 폼이 아니라 JSON 이다**(2026-09-29). 시약(`volumeByWell` 웰 맵 · `use_order`) ·
                 랩웨어(웰 기하) · 트레이 배치(묶음) · 액체 클래스 · 작업 생성 값은 모양이 깊고, 칸으로 쪼개면
                 한 칸짜리 입력이 맵을 흉내 내게 된다. 비워 두면 본문에서 빠진다(전부 선택 절이다).
              접힌 줄이 「적힘 · N줄」을 말한다 — 불러온 JSON 수백 줄이 창을 덮지 않는다(2026-09-30).
            */}
            <div className="redit__block">
              <div className="redit__listhead">
                <span className="redit__listtitle">{t('recipeEdit.jsonGroup')}</span>
              </div>
              <div className="redit__discs">
                {JSON_SECTIONS.map((section) => {
                  const value = draft.json[section.key]
                  const field = validationField?.(
                    recipeIssueKey({ group: 'other', path: section.key }),
                  )
                  return (
                    <Disclosure
                      issues={otherIssues.filter((i) => i.at.path === section.key).length}
                      key={section.key}
                      label={t(section.label)}
                      meta={
                        value.trim() === ''
                          ? t('recipeEdit.empty')
                          : t('recipeEdit.jsonFilled', { lines: lineCount(value) })
                      }
                    >
                      <Field label={t(section.label)} {...field?.fieldProps}>
                        <JsonArea
                          {...field?.inputProps}
                          gate={gate}
                          label={t(section.label)}
                          onChange={(next) =>
                            onChange({ ...draft, json: { ...draft.json, [section.key]: next } })
                          }
                          value={value}
                        />
                      </Field>
                    </Disclosure>
                  )
                })}
              </div>
            </div>
          </>
        )
      }
    }
  })()

  const locate = (fields: DraftFields, path: string) => {
    for (const g of ['centrifuge', 'assembler', 'staining'] as const) {
      if (fields === draft[g]) return recipeIssueKey({ group: g, path })
      const steps =
        g === 'staining' ? [...draft.staining.steps, ...draft.staining.moduleSteps] : draft[g].steps
      const step = steps.find((item) => item === fields)
      if (step) return recipeIssueKey({ group: g, stepKey: step.key, path })
    }
    return recipeIssueKey({ group: 'other', path })
  }
  return (
    <EditorValidation value={{ field: validationField, locate, revealErrors }}>
      <div className="redit">
        {/* 탭 줄은 본문을 내려도 위에 붙어 있다(창 본문이 스크롤 칸이다) */}
        <div className="redit__tabs">
          <Tabs
            items={GROUPS.map((g) => ({
              id: g,
              label: t(GROUP_LABEL[g]),
              mark: <IssueMark count={of(g).length} />,
            }))}
            label={t('recipeEdit.groups')}
            onChange={setGroup}
            value={group}
          />
        </div>
        <div aria-label={t(GROUP_LABEL[group])} className="redit__panel" role="tabpanel">
          {panel}
        </div>
      </div>
    </EditorValidation>
  )
}
